import { Skeleton } from "@/components/ui/skeleton";

/**
 * Admin shell skeleton — mirrors AdminLayout (sidebar + header + main).
 * Used as the top-level Suspense fallback when the admin bundle is loading
 * for the first time (deep-link into /admin before the layout mounts).
 *
 * Accepts a child skeleton to render inside the main content area.
 */
export function AdminShellSkeleton({
  children,
}: {
  children?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-light-gray dark:bg-surface">
      {/* Sidebar — hidden on mobile, visible tablet+ */}
      <aside className="fixed left-0 top-0 hidden h-full w-[360px] border-r border-outline-variant/10 bg-surface-container-lowest p-6 dark:bg-surface-container tablet:block">
        {/* Brand */}
        <div className="mb-8 flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-5 w-40" />
        </div>

        {/* Nav sections */}
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, section) => (
            <div key={section} className="space-y-2">
              <Skeleton className="mb-2 h-3 w-24" />
              {Array.from({ length: 3 }).map((_, link) => (
                <div
                  key={link}
                  className="flex items-center gap-3 rounded-lg px-3 py-2"
                >
                  <Skeleton className="h-5 w-5 rounded" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* User tile at bottom */}
        <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3 rounded-lg border border-outline-variant/10 p-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="min-h-screen tablet:ml-[360px]">
        {/* Header */}
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-outline-variant/10 bg-surface-container-lowest/80 px-4 backdrop-blur dark:bg-surface-container/80 tablet:px-6 lg:px-8">
          <Skeleton className="h-6 w-48" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-full" />
            <Skeleton className="h-9 w-9 rounded-full" />
            <Skeleton className="h-9 w-32 rounded-full" />
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 tablet:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
